import type { AppAction } from 'actions/types/ActionTypes'
import { UPDATE_LOYALTY_ESIM_CODE, SET_LOYALTY_ESIM_CRUISE_CODES } from 'actions/actionConstants'
import requestLoyaltyEsimBenefit from 'api/loyalty/requestLoyaltyEsimBenefit'
import { getEligibleEsimOrders, type CruiseCountryCodesByOrderId, type EligibleEsimOrder } from 'loyalty/lib/getEligibleEsimOrders'
import { getCruiseOfferSnapshot } from 'api/cruises'
import { getCruiseItineraryCountryCodes } from 'checkout/lib/utils/cruises/itinerary'
import { unique } from 'lib/array/arrayUtils'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { INITIAL_ESIM_PROMO_CODES_STATE } from 'reducers/loyaltyStateReducer'

function getEsimState(state: App.State): App.LoyaltyEsimPromoCodesState {
  const raw = state.loyalty.esimPromoCodes
  return raw?.codesByOrderId ? raw : INITIAL_ESIM_PROMO_CODES_STATE
}

const GENERATING_THRESHOLD_MS = 2_000
const RETRY_DELAY_MS = 3_000
const MAX_RETRIES = 40

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function fetchEsimPromoCodes(orderIdFilter?: string): AppAction {
  return async(dispatch, getState) => {
    async function pollCodeForOrder(order: EligibleEsimOrder): Promise<void> {
      for (let retryCount = 0; retryCount <= MAX_RETRIES; retryCount++) {
        const response = await requestLoyaltyEsimBenefit(order.orderId, order.customerId)

        if (getEsimState(getState()).codesByOrderId[order.orderId]?.phase === 'success') return

        switch (response.status) {
          case 'success':
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'success', code: response.result.promocode, tier: (response.result.tier as App.LoyaltyTier) ?? order.tier },
            })
            return

          case 'generating':
            if (order.tripEnded) {
              dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: order.orderId, update: { phase: 'not_found' } })
              return
            }
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'generating' },
            })
            if (retryCount < MAX_RETRIES) {
              await delay(RETRY_DELAY_MS)
              if (getEsimState(getState()).codesByOrderId[order.orderId]?.phase === 'success') return
              continue
            }
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'error' },
            })
            return

          case 'not_ready':
            if (order.tripEnded) {
              dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: order.orderId, update: { phase: 'not_found' } })
              return
            }
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'available_later', availableDate: order.availableDate },
            })
            return

          case 'not_found':
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'not_found' },
            })
            return

          default:
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'error' },
            })
            return
        }
      }
    }

    let state = getState()
    const accountRegion = getLoyaltyAccountRegion(state)
    if (!accountRegion) return

    const orders = getUpcomingOrders(state)
    if (!orders.length) return

    const ordersWithCruise = orders.filter(
      o => o.status !== 'cancelled' && o.cruiseItems?.some(item => item.status === 'completed'),
    )

    if (!getEsimState(state).cruiseCodesSettled) {
      if (ordersWithCruise.length) {
        const map: CruiseCountryCodesByOrderId = {}
        await Promise.all(
          ordersWithCruise.map(async order => {
            const completedCruiseItems = order.cruiseItems.filter(item => item.status === 'completed')
            const codes: Array<string> = []
            for (const item of completedCruiseItems) {
              if (!item.bookingId) continue
              try {
                const snapshot = await getCruiseOfferSnapshot(item.bookingId)
                codes.push(...getCruiseItineraryCountryCodes(snapshot))
              } catch {
                // skip this item
              }
            }
            if (codes.length) map[order.id] = unique(codes)
          }),
        )
        dispatch({ type: SET_LOYALTY_ESIM_CRUISE_CODES, cruiseCountryCodesByOrderId: map, cruiseCodesSettled: true })
      } else {
        dispatch({ type: SET_LOYALTY_ESIM_CRUISE_CODES, cruiseCountryCodesByOrderId: {}, cruiseCodesSettled: true })
      }
    }

    state = getState()
    const { flightDetails } = state.orders
    const { cruiseCountryCodesByOrderId } = getEsimState(state)
    const esimExcludedCountryCodes = state.loyalty.config?.esimExcludedCountryCodes

    let eligibleOrders = getEligibleEsimOrders(orders, accountRegion, flightDetails, cruiseCountryCodesByOrderId, esimExcludedCountryCodes)
    if (orderIdFilter) eligibleOrders = eligibleOrders.filter(o => o.orderId === orderIdFilter)

    for (const order of eligibleOrders) {
      const existing = getEsimState(state).codesByOrderId[order.orderId]
      if (existing && existing.phase !== 'not_found' && existing.phase !== 'error') continue

      if (order.tripEnded) {
        // Trip has ended — still call API to retrieve previously generated codes,
        // but don't show "available later" or attempt generation
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: order.orderId,
          update: {
            orderId: order.orderId,
            destination: order.destination,
            dates: order.dates,
            phase: 'loading' as const,
            tier: order.tier,
          },
        })
        pollCodeForOrder(order).catch(() => {
          dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: order.orderId, update: { phase: 'not_found' } })
        })
        continue
      }

      if (!order.isWithinSevenDays) {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: order.orderId,
          update: {
            orderId: order.orderId,
            destination: order.destination,
            dates: order.dates,
            phase: 'available_later' as const,
            availableDate: order.availableDate,
            tier: order.tier,
          },
        })
        continue
      }

      dispatch({
        type: UPDATE_LOYALTY_ESIM_CODE,
        orderId: order.orderId,
        update: {
          orderId: order.orderId,
          destination: order.destination,
          dates: order.dates,
          phase: 'loading' as const,
          tier: order.tier,
        },
      })

      setTimeout(() => {
        const s = getState()
        if (getEsimState(s).codesByOrderId[order.orderId]?.phase === 'loading') {
          dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: order.orderId, update: { phase: 'generating' } })
        }
      }, GENERATING_THRESHOLD_MS)

      pollCodeForOrder(order).catch(() => {
        dispatch({ type: UPDATE_LOYALTY_ESIM_CODE, orderId: order.orderId, update: { phase: 'error' } })
      })
    }
  }
}

export default fetchEsimPromoCodes
